import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useGetVideoAround } from "@/machines/api/queries/video.queries.ts";
import { useCursorStore } from "@/machines/context/cursor-store.ts";
import { useVideoCoverageStore } from "@/shared/stores/useVideoCoverageStore.ts";
import { useVideoLibraryStore } from "@/machines/stores/useVideoLibraryStore.ts";
import { Video } from "@/machines/types/video";

interface UseVideoDataProps {
  device_mxid: string;
  /**
   * Map live-follow only (opt-in, default off). When set: (1) a forward cursor
   * jump ahead of the fetched footage never clears the accumulated clips (so the
   * tile never blanks while the cursor sits a few minutes ahead of the newest
   * clip during upload lag), and (2) the forward-fetch query polls so new clips
   * stream in as they land. Off everywhere else — the timeline editor and
   * machine-detail keep their exact reset/seek behavior.
   */
  live?: boolean;
  /**
   * Deliberate "Go live" jump target (ms), set anew by the map each time the user
   * clicks Go-live. On change, the accumulated far-behind footage (built up while
   * reviewing an old alert) is dropped and re-fetched around this live edge, so
   * the tile lands on the NEWEST clip — same end state as a fresh live load —
   * instead of crawling forward from the alert. Null / unchanged does nothing.
   * Wired for the map primary only; the never-blank guard still applies once the
   * fresh footage lands.
   */
  jumpToLiveTime?: number | null;
}

const PREFETCH_THRESHOLD = 3; // Trigger prefetch when within 3 videos of the end
const FORWARD_WINDOW_SECONDS = 600; // 10 minutes forward
const INITIAL_WINDOW_SECONDS = 300; // 5 minutes each direction
const INITIAL_WINDOW_MS = INITIAL_WINDOW_SECONDS * 1000;
const LARGE_JUMP_BUFFER_MS = 5 * 60 * 1000; // 5 minutes buffer for detecting large jumps
const FETCH_BUCKET_MS = 30000; // Dedup window: one fetch per 30s of timeline

/**
 * Dedup key for a fetch timestamp. The cursor moves every frame during playback,
 * so requests are coalesced per 30s window — without this the same footage is
 * re-requested for every distinct millisecond.
 *
 * This is ALSO the quantum applied to the `timestamp` sent to the query (see
 * `bucketedFetchTimestamp`). The query key hashes the `Date` at full ms
 * precision, but the request serializer (`formatDateTime`) truncates it to whole
 * SECONDS — so up to 1000 distinct cache keys produced one byte-identical URL,
 * every one of them a cold miss that made the query's 1h `staleTime`/`gcTime`
 * inert. Rounding the key's timestamp to the same 30s grid the dedup Set already
 * uses makes repeated cursor positions share one key and hit the cache. The
 * fetched window is ±300s, so the ≤15s shift this introduces stays far inside it.
 */
export const toFetchBucket = (timestamp: number): number =>
  Math.round(timestamp / FETCH_BUCKET_MS) * FETCH_BUCKET_MS;

/**
 * Whether the ±INITIAL_WINDOW request window around `time` is already recorded
 * as holding no footage, so the probe can be skipped.
 *
 * Only known-EMPTY ranges suppress a request. Positive coverage is deliberately
 * NOT consulted: video rows carry short-lived signed URLs that nothing in the
 * app re-signs, so they must be re-fetched on every mount — persisted coverage
 * can never stand in for them.
 */
const isKnownEmptyProbe = (deviceMxid: string, time: number): boolean => {
  const bucket = toFetchBucket(time);
  return useVideoCoverageStore
    .getState()
    .isRangeKnownEmpty(deviceMxid, bucket - INITIAL_WINDOW_MS, bucket + INITIAL_WINDOW_MS);
};

/**
 * Rows the shared video library already holds for the ±INITIAL_WINDOW around
 * `time` — the same window this hook's own request would ask for, so adopting
 * them leaves the accumulated list exactly the shape a fetch would have given it.
 */
const libraryVideosAround = (deviceMxid: string, time: number): Video[] =>
  useVideoLibraryStore.getState().getVideosAt(deviceMxid, time, INITIAL_WINDOW_MS);

/**
 * Poll cadence for pulling newly-uploaded clips while live-following (map only).
 * Matched to the timeline's event poll: clips are short (~30s), so a slower poll
 * is standing lag — a clip that has landed but isn't in our list yet can't be
 * rolled onto.
 */
const LIVE_FOOTAGE_POLL_MS = 3_000;

/**
 * How far the cursor must move before `debouncedTime` is committed to state.
 *
 * Every commit re-renders this hook's owner — the whole video tile, controls
 * included. The consumers only need coarse positions: the fetch effect rounds to
 * a 30s bucket and the adopt window spans ±300s. 10s keeps both fed while
 * cutting live-playback re-renders from two a second to one per ten.
 */
const CURSOR_COMMIT_SLACK_MS = 10_000;

function deduplicateAndSort(videos: Video[]): Video[] {
  const seen = new Set<number>();
  const unique: Video[] = [];
  for (const video of videos) {
    if (!seen.has(video.id)) {
      seen.add(video.id);
      unique.push(video);
    }
  }
  return unique.sort((a, b) => {
    return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
  });
}

export const useVideoData = ({ device_mxid, live = false, jumpToLiveTime = null }: UseVideoDataProps) => {
  // Read the cursor from the store internally (it used to arrive as a per-tick
  // prop that re-rendered this hook's owner ~30×/sec). Everything cursor-driven
  // below recomputes imperatively from a subscription and sets state only on an
  // actual change, so smooth same-clip playback triggers NO re-render.
  const cursorStore = useCursorStore();
  const selectedTimeRef = useRef<number | null>(cursorStore.getSnapshot().selectedTime);

  const [debouncedTime, setDebouncedTime] = useState<number | null>(() => cursorStore.getSnapshot().selectedTime);
  const [isDataChanging, setIsDataChanging] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Track the timestamp we're currently fetching for (initial/jump fetch). The
  // query runs during the FIRST render, before any effect, so the known-empty
  // guard has to be applied here too — otherwise every fresh mount re-probes a
  // range a previous session already proved holds no footage.
  const [fetchTimestamp, setFetchTimestamp] = useState<number | null>(() => {
    const initial = cursorStore.getSnapshot().selectedTime;
    if (initial == null) return null;
    // Footage for this instant is already in the shared library (the timeline's
    // availability strip fetched a wider window from the same endpoint) and is
    // seeded into `accumulatedVideos` below — so there is nothing to request.
    if (useVideoLibraryStore.getState().hasVideoAt(device_mxid, initial)) return null;
    return isKnownEmptyProbe(device_mxid, initial) ? null : initial;
  });

  // Accumulated videos from multiple fetches, seeded from the shared library so a
  // tile mounting after the timeline strip has already fetched starts with
  // footage instead of empty. Library rows are deduped and sorted by start.
  const [accumulatedVideos, setAccumulatedVideos] = useState<Video[]>(() => {
    const initial = cursorStore.getSnapshot().selectedTime;
    return initial == null ? [] : libraryVideosAround(device_mxid, initial);
  });

  // Timestamp for fetching the next batch (forward direction)
  const [forwardFetchTimestamp, setForwardFetchTimestamp] = useState<Date | undefined>(undefined);

  // Track the range of videos we've fetched
  const fetchedRangeRef = useRef<{ min: number; max: number } | null>(null);

  // Track previous device to detect changes
  const prevDeviceRef = useRef(device_mxid);

  // Sticky "this is (or was) a live session for the current device". Once live,
  // a cursor ahead of loaded footage must never clear the accumulated clips —
  // not even the instant live-follow is switched off (leaving live), which would
  // otherwise unmount the <video> and deadlock it at readyState 0. Reset on
  // device change so a fresh machine starts cold.
  const hadLiveRef = useRef(false);
  useEffect(() => {
    if (live) hadLiveRef.current = true;
  }, [live]);

  // Reset everything when device changes
  useEffect(() => {
    if (prevDeviceRef.current !== device_mxid) {
      setIsDataChanging(true);
      fetchedRangeRef.current = null;
      setAccumulatedVideos([]);
      setForwardFetchTimestamp(undefined);
      setCurrentVideoIndex(0);
      fetchedTimestampsRef.current.clear();
      hadLiveRef.current = false;
      prevDeviceRef.current = device_mxid;
      const timeout = setTimeout(() => {
        setIsDataChanging(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [device_mxid]);

  // Track timestamps we've already attempted to fetch (to avoid re-fetching gaps)
  const fetchedTimestampsRef = useRef<Set<number>>(new Set());

  // Claim the 30s fetch bucket for `time`; false when it was already fetched.
  const claimFetchBucket = useCallback((time: number) => {
    const bucket = toFetchBucket(time);
    if (fetchedTimestampsRef.current.has(bucket)) return false;
    fetchedTimestampsRef.current.add(bucket);
    return true;
  }, []);
  // Deliberate "Go live": drop the stale far-behind accumulation and re-fetch
  // around the live edge, so the tile lands on the newest clip (like a fresh load)
  // rather than crawling forward from the alert. Clearing the accumulation
  // unmounts the old clip so its playback can't drag the cursor back to it;
  // isDataChanging shows the spinner over the brief gap; forcing debouncedTime +
  // fetchTimestamp to the jump target fetches around NOW immediately (not the
  // stale cursor). Keeps hadLiveRef true — we're still live.
  //
  // Starts at null, NOT at the incoming prop. Seeding it from the prop meant a
  // tile that MOUNTED with a jump already pending treated it as one it had
  // handled, and silently skipped it — which is the common case whenever the
  // page asks for live before the tile exists: returning to /map via the router
  // resolves the machine from cache, so `goLiveTime` is set a beat before the
  // video mounts. The tile then began wherever the cursor seed left it and
  // crawled forward, on a page that had just asked to be live.
  const prevJumpToLiveRef = useRef<number | null>(null);
  useEffect(() => {
    if (jumpToLiveTime == null) return;
    // Only a genuinely NEW target drops the accumulation and re-seeds the fetch.
    if (jumpToLiveTime !== prevJumpToLiveRef.current) {
      prevJumpToLiveRef.current = jumpToLiveTime;
      setIsDataChanging(true);
      setAccumulatedVideos([]);
      fetchedRangeRef.current = null;
      setForwardFetchTimestamp(undefined);
      fetchedTimestampsRef.current.clear();
      setCurrentVideoIndex(0);
      setDebouncedTime(jumpToLiveTime);
      setFetchTimestamp(jumpToLiveTime);
    }
    // ...but the clear is armed on EVERY run, including the one the guard above
    // skips. `prevJumpToLiveRef` outlives the effect's cleanup — StrictMode tears
    // the effect down and re-runs it while refs persist — so arming the timer
    // only on the new-target path meant: run 1 set the flag and armed the clear,
    // the cleanup cancelled it, and run 2 saw its own value in the ref and
    // returned before re-arming. `isDataChanging` then stayed true for the life
    // of the tile, and the map's live view showed "Updating video..." forever on
    // top of footage it had already loaded (`currentVideo` set, isLoading false).
    // Only live tiles hit this: followers are passed a null target.
    const timeout = setTimeout(() => setIsDataChanging(false), 1000);
    return () => clearTimeout(timeout);
  }, [jumpToLiveTime]);

  // Adopt whatever the shared video library already holds around the current
  // instant. The timeline's availability strip pulls the same rows over a wider
  // window, so a tile mounting alongside it — or moving onto a stretch the strip
  // already fetched — gets its footage without touching the network. Subscribed
  // rather than read once, so rows that land AFTER this tile mounted (the usual
  // race on a fresh /map load) are picked up as soon as they arrive.
  useEffect(() => {
    if (debouncedTime == null) return;
    const adopt = () => {
      const rows = libraryVideosAround(device_mxid, debouncedTime);
      if (rows.length === 0) return;
      setAccumulatedVideos((prev) => {
        const merged = deduplicateAndSort([...prev, ...rows]);
        // Nothing new: keep the previous array so the <video> element, the clip
        // index and every downstream memo stay untouched.
        return merged.length === prev.length ? prev : merged;
      });
    };
    adopt();
    return useVideoLibraryStore.subscribe(adopt);
  }, [device_mxid, debouncedTime]);

  // Smart fetch: update fetch timestamp if selected time is outside range or no video covers it
  useEffect(() => {
    if (!debouncedTime) {
      setFetchTimestamp(null);
      return;
    }

    // Already covered by the shared library — the effect above hands those rows
    // to this tile, so a request would only re-fetch what we can already show.
    // Checked before the range heuristics because on the first commit after an
    // adopt this effect still closes over the pre-adopt `accumulatedVideos`.
    if (useVideoLibraryStore.getState().hasVideoAt(device_mxid, debouncedTime)) return;

    // If we don't have a fetched range yet, fetch immediately
    if (!fetchedRangeRef.current) {
      if (!isKnownEmptyProbe(device_mxid, debouncedTime)) setFetchTimestamp(debouncedTime);
      return;
    }

    const { min, max } = fetchedRangeRef.current;

    // Large time jump: if selected time is far outside accumulated range, reset
    if (debouncedTime < min - LARGE_JUMP_BUFFER_MS || debouncedTime > max + LARGE_JUMP_BUFFER_MS) {
      // Live map primary (or a live session we just left): a cursor sitting ahead
      // of the newest clip (the event-edge seed, upload lag, or leaving live
      // before playback caught up) must NEVER clear the accumulated footage —
      // that unmounts the <video> and deadlocks it at readyState 0 with no
      // recovery. Keep the last clip loaded so the tile never blanks; the forward
      // poll advances the edge as clips arrive. (A deliberate "Go live" jump is a
      // separate, explicit reset — see the jump-to-live effect.) Only a genuinely
      // cold (never-live) path — the editor / machine-detail — resets on a jump.
      if ((live || hadLiveRef.current) && debouncedTime > max) {
        // Never blank — but DO go looking. Stepping FORWARD to a newer alert
        // (the card's Next button) is a jump past `max` too, and bailing outright
        // meant that alert's footage was never requested: the tile kept showing
        // the old clip while the timeline and card had moved on. The invariant
        // this guard protects is "don't CLEAR the accumulation", not "don't
        // fetch", so probe around the new instant and let the rows merge in.
        // Dedup keeps this to one request per 30s bucket, so a cursor parked
        // ahead of the live edge during upload lag still costs a single probe.
        if (!isKnownEmptyProbe(device_mxid, debouncedTime) && claimFetchBucket(debouncedTime)) {
          setFetchTimestamp(debouncedTime);
        }
        return;
      }
      setAccumulatedVideos([]);
      setForwardFetchTimestamp(undefined);
      fetchedRangeRef.current = null;
      fetchedTimestampsRef.current.clear();
      if (!isKnownEmptyProbe(device_mxid, debouncedTime)) setFetchTimestamp(debouncedTime);
      return;
    }

    // Already on screen? Nothing to fetch. This MUST be tested before any
    // range/buffer heuristic: at the live edge (and at the end of footage) `max`
    // stops advancing, so the old 60s buffer-edge branch was permanently true
    // and re-requested a window once per 30s bucket FOREVER, even though a
    // loaded clip covered the instant. Look-ahead is the forward-prefetch
    // query's job (it polls while live), not this effect's — so once the
    // timestamp is covered there is no reason to touch the network.
    const hasVideoAtTime = accumulatedVideos.some((video) => {
      const start = new Date(video.start_time).getTime();
      const end = new Date(video.end_time).getTime();
      return debouncedTime >= start && debouncedTime <= end;
    });
    if (hasVideoAtTime) return;

    // Not covered: either a gap inside the loaded range, or the cursor has run
    // off the end of the footage (live catch-up). Both want the same thing — one
    // probe per 30s bucket, the dedup Set being the backstop against the cursor
    // ticking twice a second through the same uncovered stretch.
    if (isKnownEmptyProbe(device_mxid, debouncedTime)) return;
    if (claimFetchBucket(debouncedTime)) {
      setFetchTimestamp(debouncedTime);
    }
  }, [debouncedTime, accumulatedVideos, live, claimFetchBucket, device_mxid]);

  // Quantized to the 30s grid so the query KEY has the same resolution the
  // request URL actually has (see `toFetchBucket`) — without this every cursor
  // millisecond minted a fresh cache key for a byte-identical request.
  const bucketedFetchTimestamp = fetchTimestamp ? toFetchBucket(fetchTimestamp) : null;

  // Initial/jump fetch query. Explicitly disabled without a timestamp: the
  // cursor is null until it is seeded (and stays null when the library already
  // covers the instant), and `/videos/around` REJECTS a timestamp-less request
  // with a 400 — the query's own default would still have sent it.
  const { data, status, fetchStatus } = useGetVideoAround(
    {
      page: 1,
      itemsPerPage: 20,
      timestamp: bucketedFetchTimestamp ? new Date(bucketedFetchTimestamp) : undefined,
      timestamp_before: INITIAL_WINDOW_SECONDS,
      timestamp_after: INITIAL_WINDOW_SECONDS,
      device_mxid,
    },
    { enabled: bucketedFetchTimestamp !== null && !!device_mxid },
  );

  // Forward-fetch query (only enabled when forwardFetchTimestamp is set). While
  // live-following, poll it so newly-uploaded clips stream in without a cursor
  // move (the tile is stalled at the live edge waiting for footage).
  const { data: forwardData } = useGetVideoAround(
    {
      page: 1,
      itemsPerPage: 20,
      timestamp: forwardFetchTimestamp,
      timestamp_before: 0,
      timestamp_after: FORWARD_WINDOW_SECONDS,
      device_mxid,
    },
    {
      enabled: !!forwardFetchTimestamp && !!device_mxid,
      refetchInterval: live ? LIVE_FOOTAGE_POLL_MS : false,
    },
  );

  // Merge initial fetch data into accumulated videos, and record what the probe
  // learned about this device's timeline (timestamps only — never the rows).
  useEffect(() => {
    const videos = data?.data;
    if (!videos) return;

    if (videos.length === 0) {
      // A settled window that genuinely holds no footage: remember it so future
      // mounts don't re-probe the same emptiness. `recordEmpty` itself ignores
      // ranges too close to now, where "empty" only means "not uploaded yet".
      if (status === "success" && bucketedFetchTimestamp !== null) {
        useVideoCoverageStore
          .getState()
          .recordEmpty(
            device_mxid,
            bucketedFetchTimestamp - INITIAL_WINDOW_MS,
            bucketedFetchTimestamp + INITIAL_WINDOW_MS,
          );
      }
      return;
    }

    let minStart = Infinity;
    let maxEnd = -Infinity;
    for (const video of videos) {
      minStart = Math.min(minStart, new Date(video.start_time).getTime());
      maxEnd = Math.max(maxEnd, new Date(video.end_time).getTime());
    }
    if (Number.isFinite(minStart) && Number.isFinite(maxEnd)) {
      useVideoCoverageStore.getState().recordCovered(device_mxid, minStart, maxEnd);
    }
    // Share the rows so sibling tiles / the availability strip can skip a request.
    useVideoLibraryStore.getState().addVideos(device_mxid, videos);

    setAccumulatedVideos((prev) => {
      // If prev is empty, just use the new data
      if (prev.length === 0) return deduplicateAndSort(videos);
      return deduplicateAndSort([...prev, ...videos]);
    });
  }, [data?.data, status, bucketedFetchTimestamp, device_mxid]);

  // Merge forward fetch data into accumulated videos
  useEffect(() => {
    const videos = forwardData?.data;
    if (!videos || videos.length === 0) return;

    // Newly-uploaded clips from the live-follow prefetch — the freshest rows in
    // the app; share them so every other consumer of this camera gets them too.
    useVideoLibraryStore.getState().addVideos(device_mxid, videos);
    setAccumulatedVideos((prev) => deduplicateAndSort([...prev, ...videos]));
  }, [forwardData?.data, device_mxid]);

  // Build sorted videos from accumulated list and update fetched range
  const sortedVideos = useMemo(() => {
    if (accumulatedVideos.length === 0) {
      fetchedRangeRef.current = null;
      return [];
    }

    // accumulatedVideos is already deduped and sorted
    const minTime = new Date(accumulatedVideos[0].start_time).getTime();
    const maxTime = new Date(accumulatedVideos[accumulatedVideos.length - 1].end_time).getTime();
    fetchedRangeRef.current = { min: minTime, max: maxTime };

    return accumulatedVideos;
  }, [accumulatedVideos]);

  // Keep the latest sorted list + live flag in refs so the mount-stable cursor
  // subscription can resolve the clip index against current data without
  // re-subscribing on every list/flag change.
  const sortedVideosRef = useRef(sortedVideos);
  sortedVideosRef.current = sortedVideos;
  const liveRef = useRef(live);
  liveRef.current = live;

  // Trigger forward-fetch when approaching the end of the list
  useEffect(() => {
    if (sortedVideos.length === 0) return;

    if (currentVideoIndex >= sortedVideos.length - PREFETCH_THRESHOLD) {
      const lastVideo = sortedVideos[sortedVideos.length - 1];
      const lastEndTime = new Date(lastVideo.end_time);
      setForwardFetchTimestamp(lastEndTime);
    }
  }, [currentVideoIndex, sortedVideos]);

  // Resolve which clip contains the current cursor, reading the latest cursor +
  // data from refs so it can run from the mount-stable cursor subscription below.
  // Sets the index ONLY when it actually changes (the functional updater returns
  // the same value otherwise → React bails), so smooth same-clip playback never
  // re-renders this hook's owner.
  const resolveVideoIndex = useCallback(() => {
    const sorted = sortedVideosRef.current;
    const st = selectedTimeRef.current;

    let nextIndex = 0;
    if (sorted.length !== 0 && st) {
      // Find the video that contains the selected timestamp
      const videoIndex = sorted.findIndex((video) => {
        const videoStart = new Date(video.start_time).getTime();
        const videoEnd = new Date(video.end_time).getTime();
        return st >= videoStart && st <= videoEnd;
      });

      if (videoIndex !== -1) {
        nextIndex = videoIndex;
      } else {
        // Not inside any clip. Live map primary (or a live session we've left,
        // e.g. paused): resolve DETERMINISTICALLY to a real loaded clip so
        // `currentVideo` is never undefined and the render never falls to "No
        // video available" while footage exists — a cursor ahead of all footage
        // (the event-edge seed) selects the NEWEST clip so playback starts and its
        // RAF loop carries the cursor forward onto real footage. Uses `hadLiveRef`
        // so the clamp survives the leave-live transition (pausing), where `live`
        // drops to false but the cursor may still sit ahead of footage.
        const isLive = liveRef.current || hadLiveRef.current;
        const firstStart = new Date(sorted[0].start_time).getTime();
        const lastEnd = new Date(sorted[sorted.length - 1].end_time).getTime();
        if (isLive && st >= lastEnd) {
          nextIndex = sorted.length - 1;
        } else if (isLive && st <= firstStart) {
          nextIndex = 0;
        } else {
          // Closest clip (also the in-a-gap fall-through for the live branch).
          let closestIndex = 0;
          let minDiff = Infinity;
          sorted.forEach((video, index) => {
            const videoStart = new Date(video.start_time).getTime();
            const diff = Math.abs(st - videoStart);
            if (diff < minDiff) {
              minDiff = diff;
              closestIndex = index;
            }
          });
          nextIndex = closestIndex;
        }
      }
    }

    setCurrentVideoIndex((prev) => (prev === nextIndex ? prev : nextIndex));
  }, []);

  // Single cursor subscription (mount-stable): track the latest cursor, debounce
  // it into `debouncedTime` (the fetch window; fires at most every 500ms, never
  // per frame — same as the old `[selectedTime]` debounce effect), and re-resolve
  // the clip index. `setCurrentVideoIndex` no-ops on an unchanged index, so this
  // does not re-render during smooth same-clip playback.
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const onCursor = () => {
      selectedTimeRef.current = cursorStore.getSnapshot().selectedTime;
      // THROTTLE, not debounce — leave a pending timer alone instead of
      // re-arming it.
      //
      // Clearing it on every tick made this fire at most once per 500ms of
      // SILENCE, and the cursor is never silent while anything plays: the
      // video's RAF loop writes it ~30x/sec, so the timer was destroyed every
      // ~33ms and never fired at all. `debouncedTime` then only moved when
      // playback stopped — and both the fetch effect and the library-adopt
      // subscription key on it, so neither window advanced while live.
      //
      // Followers feel it first. They have no forward-prefetch query of their
      // own (that is gated on `live`), so a frozen window is their only source
      // of new clips: measured on Casa de Pedra, a follower sat at 30.0/30 with
      // its list stuck at 10 clips while the primary played on past 24.
      if (debounceTimerRef.current == null) {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          setDebouncedTime((prev) => {
            const next = selectedTimeRef.current;
            // ...but only COMMIT a materially different value. Firing every
            // 500ms fixed the frozen window and replaced it with a re-render
            // twice a second on every tile — the whole video surface and its
            // control bar rebuilding continuously, which reads as a laggy,
            // flickering follower.
            //
            // Nothing downstream wants that resolution. The fetch effect
            // quantises to a 30s bucket (`toFetchBucket`) and the library adopt
            // window spans ±300s, so a few seconds of slack costs those nothing
            // and removes ~95% of the renders. A real seek moves far more than
            // the threshold and still lands within one timer tick.
            if (prev != null && next != null && Math.abs(next - prev) < CURSOR_COMMIT_SLACK_MS) {
              return prev;
            }
            return next;
          });
        }, 500);
      }
      resolveVideoIndex();
    };
    const unsub = cursorStore.subscribe(onCursor);
    return () => {
      unsub();
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [cursorStore, resolveVideoIndex]);

  // Re-resolve the clip index when the loaded list or live flag changes (the old
  // effect had `sortedVideos`/`live` deps alongside the cursor). Also covers mount.
  useEffect(() => {
    resolveVideoIndex();
  }, [sortedVideos, live, resolveVideoIndex]);

  // "Is (or was) a live session for this device." The clamp/seek guards must stay
  // engaged through and after the leave-live transition (pausing flips `live`
  // false while the cursor may still sit ahead of footage), so gate on this
  // instead of the instantaneous `live`. Cold editor/machine-detail paths never
  // set `hadLiveRef`, so this stays false there.
  const liveClampActive = live || hadLiveRef.current;

  // Get current video. Live (or was-live) map primary: clamp the index into range
  // so a stale out-of-range index can never resolve to `undefined` while footage
  // exists (which would unmount the <video> and strand it at readyState 0). A
  // no-op when the index is already valid, and untouched off the live path.
  const currentVideo =
    liveClampActive && sortedVideos.length > 0
      ? sortedVideos[Math.min(Math.max(currentVideoIndex, 0), sortedVideos.length - 1)]
      : sortedVideos[currentVideoIndex];

  // Newest available footage: the start/end (ms) of the last accumulated clip.
  // The map live-follow path reports these up so the shared cursor can be pinned
  // to real footage (start = where playback resumes on go-live / new footage;
  // end = the true live edge). Null until any footage is loaded.
  const lastVideo = sortedVideos[sortedVideos.length - 1];
  const footageStartMs = lastVideo ? new Date(lastVideo.start_time).getTime() : null;
  const footageEndMs = lastVideo ? new Date(lastVideo.end_time).getTime() : null;

  // Navigation handlers
  const handlePrevVideo = useCallback(() => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(prev => prev - 1);
    }
  }, [currentVideoIndex]);

  const handleNextVideo = useCallback(() => {
    if (currentVideoIndex < sortedVideos.length - 1) {
      setCurrentVideoIndex(prev => prev + 1);
    }
  }, [currentVideoIndex, sortedVideos.length]);

  // Jump straight to a clip by index (live-follow re-anchors to the newest clip
  // on every rollover rather than stepping one forward — see handleVideoEnded).
  const handleGoToVideo = useCallback((index: number) => {
    setCurrentVideoIndex(Math.min(Math.max(index, 0), Math.max(sortedVideos.length - 1, 0)));
  }, [sortedVideos.length]);

  return {
    currentVideo,
    sortedVideos,
    currentVideoIndex,
    // React Query holds a DISABLED query at status "pending" indefinitely, so
    // "pending" alone can no longer mean "loading": the initial query is disabled
    // until there is a timestamp to send, and never runs at all when the shared
    // library already covers the instant. Gate on an actually in-flight fetch —
    // otherwise the spinner sits permanently over footage the tile already holds.
    isLoading: status === "pending" && fetchStatus !== "idle",
    isDataChanging,
    handlePrevVideo,
    handleNextVideo,
    handleGoToVideo,
    footageStartMs,
    footageEndMs,
    // True while live or after leaving live (until device change) — the video
    // component gates its live-only seek guards on this so they don't drop at the
    // leave-live moment. False on cold editor/machine-detail paths.
    liveClampActive,
  };
};
